"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { ChartLineIcon } from "lucide-react";
import { useId } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useVisualizationUiContext } from "@/lib/ai/visualization/client/ui-context";
import { cn } from "@/lib/utils";

/**
 * チャットヘッダーへ差し込む visualization 専用トグル。
 *
 * @remarks
 * ボタンの copy と beta 表現は plugin 側へ閉じ込め、shared header には
 * action slot だけを残す。
 */
export function VisualizationHeaderAction() {
  const { i18n } = useLingui();
  const { isVisualizationEnabled, setVisualizationEnabled } =
    useVisualizationUiContext();
  const switchId = useId();

  const visualizationToggleLabel = isVisualizationEnabled
    ? i18n._(msg`Visualization: ON`)
    : i18n._(msg`Visualization: OFF`);
  const visualizationToggleTitle = i18n._(msg`Table & chart`);
  const visualizationToggleTooltipTitle = `${visualizationToggleTitle} (Beta)`;
  const visualizationToggleTooltipDescription = i18n._(
    msg`When enabled, AI responses can include tables or charts when suitable data is detected.`,
  );
  const visualizationToggleTooltipRoadmap = i18n._(
    msg`We plan to keep improving chart readability and the choice between tables and charts based on context.`,
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "relative inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 pr-1.5 text-[11px] font-semibold leading-none transition-colors duration-150",
            isVisualizationEnabled
              ? "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary"
              : "bg-transparent text-muted-foreground hover:bg-accent/30 hover:text-foreground",
          )}
        >
          <Label
            htmlFor={switchId}
            className="inline-flex h-4 translate-y-[2px] cursor-pointer items-center gap-1 text-[11px] font-semibold leading-none text-inherit"
          >
            <ChartLineIcon
              aria-hidden
              className="size-3.5 shrink-0 -translate-y-px opacity-80"
            />
            <span className="inline-flex items-center leading-none">
              {visualizationToggleTitle}
            </span>
          </Label>
          <span className="pointer-events-none absolute -top-2 -right-0.5 rounded-full bg-gradient-to-r from-yellow-400 to-orange-400 px-1 py-[1px] text-[8px] font-semibold leading-none text-white shadow-sm">
            Beta
          </span>
          <Switch
            id={switchId}
            checked={isVisualizationEnabled}
            aria-label={visualizationToggleLabel}
            className="h-[15px] w-[25px] shrink-0 translate-y-px self-center data-[state=unchecked]:bg-muted-foreground/30 [&>span]:h-[11px] [&>span]:w-[11px] [&>span[data-state=checked]]:translate-x-[10px]"
            onCheckedChange={setVisualizationEnabled}
          />
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="end"
        className="max-w-[248px] space-y-1.5"
      >
        <p className="text-xs font-semibold">
          {visualizationToggleTooltipTitle}
        </p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {visualizationToggleTooltipDescription}
        </p>
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          {visualizationToggleTooltipRoadmap}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
